import { FC, useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import TitleBar from '../TitleBar';
import BottomNavigation from '../BottomNavigation';
import CostAndResources from './components/CostAndResources';
import { RootState } from 'store';
import { saveCostAndResources } from 'store/ohaFormReducer';
import { costAndResourcesSchema } from '../../schema/costAndResources';
import { useOhaFormStep } from 'screens/Mutations/hooks/useOhaFormStep';
import { mapOHAForm } from 'screens/Mutations/utils/mappers/oha/mapFormToApi';
import { FormStepProps } from 'screens/Mutations/utils/types';
import FormTemplate from 'components/Templates/FormTemplate';
import FormStepper from '../FormStepper';
import { Form } from 'components/Molecules/components/Form/Form.styled';
import CustomSnackbar from 'components/Snackbar/CustomSnackbar';

const CostAndResourcesForm: FC<FormStepProps> = ({ title, subtitle }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [snackbar, setSnackbar] = useState<{ status: string; name: string } | undefined>();
  const ohaFormState = useSelector((state: RootState) => state.ohaForm);

  const formMethods = useForm({
    defaultValues: { ...ohaFormState.costAndResources },
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(costAndResourcesSchema),
  });

  const {
    handleSubmit,
    reset,
    watch,
    formState: { isDirty },
  } = formMethods;

  const hasBuyoutAmount = watch('hasBuyoutAmount');
  const typeOfBuyoutFinancing = watch('typeOfBuyoutFinancing');
  const costAndResourcesFormValues = watch();

  const completeForm = {
    ...ohaFormState,
    costAndResources: costAndResourcesFormValues,
  };

  const onSaveError = () => handleNotification('error', 'submit');

  const { save, isSaving, goToNextStep } = useOhaFormStep({ onSaveError });

  const onSubmit = async () => {
    dispatch(saveCostAndResources(costAndResourcesFormValues));

    if (isDirty) {
      await save(mapOHAForm(completeForm));
    }
    goToNextStep();
  };

  const dispatchForm = useCallback(() => {
    dispatch(saveCostAndResources(costAndResourcesFormValues));
  }, [costAndResourcesFormValues, dispatch]);

  const handleNotification = useCallback(
    (status: string, name: string) => setSnackbar({ status, name }),
    []
  );

  const handleOnClose = useCallback(() => setSnackbar(undefined), []);

  return (
    <>
      <FormProvider {...formMethods}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormTemplate
            header={
              <TitleBar
                onResult={handleNotification}
                formState={completeForm}
                resetForm={reset}
                isFormDirty={isDirty}
                dispatchCurrentForm={dispatchForm}
              />
            }
            sidebar={<FormStepper />}
            footer={
              <BottomNavigation dispatchCurrentForm={dispatchForm} isLoadingSubmit={isSaving} />
            }
          >
            <CostAndResources
              title={title}
              subtitle={subtitle}
              hasBuyoutAmount={hasBuyoutAmount}
              typeOfBuyoutFinancing={typeOfBuyoutFinancing}
            />
          </FormTemplate>
        </Form>
      </FormProvider>

      {snackbar && (
        <CustomSnackbar
          isOpen={!!snackbar}
          handleClose={handleOnClose}
          severity={snackbar.status === 'success' ? 'success' : 'error'}
          message={t(`directMutations.snackbar.${snackbar.name}.${snackbar.status}`)}
        />
      )}
    </>
  );
};

export default CostAndResourcesForm;
